import { proTeams, type ManagedTeam, type TeamCommand, type TeamOperation } from "../../api/proTeams";
import { ApiError } from "../../api/client";
import { failureMessage, validSteamId } from "./state";

export function lineupIds(input: string): string[] {
  const ids = [...new Set(input.split(/[\s,;]+/).filter(Boolean))];
  if (!ids.length || ids.length > 20 || ids.some(id => !validSteamId(id)))
    throw new Error("Enter 1–20 valid SteamID64 values, separated by spaces, commas, or new lines.");
  return ids;
}

export function removablePlayers(team: ManagedTeam): string[] {
  return team.snapshot.members.filter(m => !m.admin && m.steam_id !== team.snapshot.bot_steam_id).map(m => m.steam_id);
}

export type RosterRow = {
  steamId: string;
  key: string;
  state: "waiting" | "working" | "done" | "failed" | "attention";
  message?: string;
  body?: TeamCommand;
  operationId?: string;
};
export type RosterBatch = { action: "invite" | "kick"; teamId: number; rows: RosterRow[] };

// Save the exact body/key before dispatch. An interrupted request is resumed with
// the same key, never silently retried as a new remote mutation.
export async function runRosterBatch(batch: RosterBatch, update: (value: RosterBatch) => void, stopped: () => boolean,
  api = proTeams, wait = () => new Promise<void>(resolve => setTimeout(resolve, 1200))) {
  const next: RosterBatch = structuredClone(batch);
  const publish = () => update(structuredClone(next));
  for (const row of next.rows) {
    if (stopped()) break;
    if (row.state === "done" || row.state === "failed") continue;
    const resuming = !!row.body || !!row.operationId;
    let submitting = false;
    try {
      if (!row.body && !row.operationId) {
        const team = await api.get(next.teamId);
        if (stopped()) break;
        if (batch.action === "invite" && team.snapshot.members.some(m => m.steam_id === row.steamId)) {
          row.state = "done"; row.message = "Already joined"; publish(); continue;
        }
        if (batch.action === "kick" && !removablePlayers(team).includes(row.steamId)) {
          row.state = "done"; row.message = "Already absent or protected"; publish(); continue;
        }
        row.body = { steam_id: row.steamId, ...(batch.action === "kick" ? { expected_revision: team.revision } : {}) };
      }
      row.state = "working"; row.message = "Checking request…"; publish();
      submitting = !row.operationId;
      let op: TeamOperation = row.operationId ? await api.operation(row.operationId)
        : await api.submit(next.action, next.teamId, row.body!, row.key);
      submitting = false;
      row.operationId = op.operation_id; publish();
      let polls = 0;
      while (op.status === "queued" || op.status === "running") {
        if (stopped()) return next;
        if (++polls > 75) throw new Error("Still processing. Continue to check this request before sending the remaining players.");
        await wait();
        op = await api.operation(op.operation_id);
      }
      row.message = op.message || op.code || (next.action === "invite" ? "Invitation sent · acceptance unconfirmed" : "Removed");
      row.state = op.status === "succeeded" ? "done" : op.status === "failed" ? "failed" : "attention";
      publish();
      if (op.status === "unknown" || (op.status === "failed" && (next.action === "kick" || op.code === "bot_quarantined"))) break;
    } catch (error) {
      row.state = submitting && !resuming && error instanceof ApiError && error.status >= 400 && error.status < 500 ? "failed" : "attention";
      row.message = failureMessage(error); publish(); break;
    }
  }
  return next;
}
